import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, X, AlertTriangle, FolderPlus, ChevronDown, ChevronRight, FileSpreadsheet } from "lucide-react";
import {
  getCategoriasImpostos, postCategoriaImposto, putCategoriaImposto, deleteCategoriaImposto,
  getImpostos, postImposto, putImposto, deleteImposto,
  getImpostosCalculado,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import ComboBoxFiltravel from "../../componets/ComboBoxFiltravel";
import { KpiRow } from "../../components/dashboard/KpiCard";
import {
  MESES_ABREV, formatCurrency,
  calcValorUnitario, calcValorMensalBase, calcProjecaoMensal, calcTotalAnual, somarProjecoes,
} from "../../utils/orcamento";

// Impostos: mesma lógica de categoria pai > subcategoria > item (com inflação/reajuste)
// de Custos Gerais, mas geral — não escopada por departamento (não há seletor de
// departamento nesta tela).

// Paleta padrão da plataforma (mesma usada em Custos Gerais/Despesas Gerais).
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
  categoriaBg: isLight ? "#EDE0FB" : "#251A45",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
  success: "#10B981",
  danger: "#EF4444",
});

// Subcategorias fixas do seed inicial (ver backend/app.py) — é nelas que os itens calculados
// por empresa (IRPJ, CSLL) entram, ao lado dos itens manuais já cadastrados.
const CODIGO_SUBCATEGORIA_IRPJ = "5.1.01";
const CODIGO_SUBCATEGORIA_CSLL = "5.1.02";

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const OPCOES_MES = MESES_ABREV.map((label, i) => ({ value: i + 1, label }));

const FORM_ITEM_VAZIO = {
  categoria_id: "", nome: "",
  valor_atual: "", mes_inicio_valor: "", mes_fim_valor: "",
  qtde_usuario_atual: "1",
  reajustes: [], expansoes: [],
};
const FORM_CATEGORIA_VAZIO = { codigo: "", nome: "", categoria_pai_id: "", ordem: "0" };

// Só entram no payload/preview as linhas com os 3 campos preenchidos — uma linha em
// preenchimento (ex: só o mês escolhido) fica de fora até ficar completa.
function reajustesDoForm(form) {
  return (form.reajustes || [])
    .filter((r) => r.tipo_indice && r.mes && r.percentual !== "" && !Number.isNaN(parseFloat(r.percentual)))
    .map((r) => ({ tipo_indice: r.tipo_indice, mes: parseInt(r.mes, 10), percentual: parseFloat(r.percentual) }));
}

function resumoReajustes(reajustes) {
  return (reajustes || [])
    .map((r) => `${MESES_ABREV[r.mes - 1]} ${r.tipo_indice === "inflacao" ? "Inflação" : "Reajuste"} +${Number(r.percentual).toFixed(2)}%`)
    .join(" · ");
}

// Só entram no payload/preview as linhas com os 3 campos preenchidos — uma linha em
// preenchimento fica de fora até ficar completa.
function expansoesDoForm(form) {
  return (form.expansoes || [])
    .filter((e) => e.qtde_usuario_novo !== "" && e.mes_inicio && e.mes_fim && !Number.isNaN(parseInt(e.qtde_usuario_novo, 10)))
    .map((e) => ({
      qtde_usuario_novo: parseInt(e.qtde_usuario_novo, 10),
      mes_inicio: parseInt(e.mes_inicio, 10),
      mes_fim: parseInt(e.mes_fim, 10),
    }));
}

function resumoExpansoes(expansoes) {
  return (expansoes || [])
    .map((e) => `${MESES_ABREV[e.mes_inicio - 1]}–${MESES_ABREV[e.mes_fim - 1]}: ${e.qtde_usuario_novo}`)
    .join(" · ");
}

function construirItemPreview(form) {
  return {
    valor_atual: parseFloat(form.valor_atual) || 0,
    mes_inicio_valor: form.mes_inicio_valor ? parseInt(form.mes_inicio_valor, 10) : null,
    mes_fim_valor: form.mes_fim_valor ? parseInt(form.mes_fim_valor, 10) : null,
    qtde_usuario_atual: parseInt(form.qtde_usuario_atual, 10) || 0,
    reajustes: reajustesDoForm(form),
    expansoes: expansoesDoForm(form),
  };
}

export default function Impostos() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [categorias, setCategorias] = useState([]);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [selectedItemId, setSelectedItemId] = useState(null);
  const [categoriasColapsadas, setCategoriasColapsadas] = useState(() => new Set());
  const [impostosCalculados, setImpostosCalculados] = useState([]);

  function toggleCategoria(categoriaId) {
    setCategoriasColapsadas((prev) => {
      const next = new Set(prev);
      if (next.has(categoriaId)) next.delete(categoriaId);
      else next.add(categoriaId);
      return next;
    });
  }

  const [modalItemAberto, setModalItemAberto] = useState(false);
  const [editandoItem, setEditandoItem] = useState(null);
  const [formItem, setFormItem] = useState(FORM_ITEM_VAZIO);
  const [erroFormItem, setErroFormItem] = useState("");
  const [salvandoItem, setSalvandoItem] = useState(false);

  const [modalCategoriaAberto, setModalCategoriaAberto] = useState(false);
  const [editandoCategoria, setEditandoCategoria] = useState(null);
  const [formCategoria, setFormCategoria] = useState(FORM_CATEGORIA_VAZIO);
  const [erroFormCategoria, setErroFormCategoria] = useState("");
  const [salvandoCategoria, setSalvandoCategoria] = useState(false);

  async function carregarTudo(anoParam) {
    setLoading(true);
    setErro("");
    try {
      const [cats, its, calculado] = await Promise.all([
        getCategoriasImpostos(),
        getImpostos(anoParam),
        getImpostosCalculado(anoParam),
      ]);
      setCategorias(cats || []);
      setItens(its || []);
      setImpostosCalculados(calculado || []);
      // Categorias pai e subcategorias começam todas recolhidas ao carregar a página.
      setCategoriasColapsadas(new Set((cats || []).map((c) => c.id)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setSelectedItemId(null);
    setCategoriasColapsadas(new Set());
    carregarTudo(ano);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  // ── Itens com campos calculados ──────────────────────────────────────────
  const itensComCalculo = useMemo(() => itens.map((item) => {
    const valorUnitario = calcValorUnitario(item);
    const valorBase = calcValorMensalBase(item);
    const projecao = calcProjecaoMensal(item);
    const total = calcTotalAnual(projecao);
    return { ...item, valorUnitario, valorBase, projecao, total };
  }), [itens]);

  // ── Cascata de imposto calculada por empresa (DRE por Empresa) — entra direto na árvore de
  // categorias abaixo, como "itens" sintéticos das subcategorias 5.1.01 IRPJ / 5.1.02 CSLL
  // (mesmas 2 subcategorias já existentes) — nunca editável/excluível aqui, sempre recalculada
  // (ver services/dre_empresa_svc.py). IRPJ Adicional soma dentro do valor de IRPJ (não há
  // subcategoria própria — mesmas 2 linhas que já existiam antes deste módulo).
  const itensCalculadosPorSubcategoria = useMemo(() => {
    const porEmpresa = {};
    for (const linha of impostosCalculados) {
      if (!porEmpresa[linha.empresa]) porEmpresa[linha.empresa] = [];
      porEmpresa[linha.empresa].push(linha);
    }
    for (const linhas of Object.values(porEmpresa)) linhas.sort((a, b) => a.mes - b.mes);

    function itemCalculado(codigoSubcategoria, empresa, linhas, valorDoMes) {
      const projecao = linhas.map(valorDoMes);
      return {
        id: `calculado-${codigoSubcategoria}-${empresa}`,
        nome: empresa,
        valorBase: null, mes_inicio_valor: null, mes_fim_valor: null,
        qtde_usuario_atual: null, valorUnitario: null,
        expansoes: [], reajustes: [],
        projecao, total: calcTotalAnual(projecao),
        calculado: true,
      };
    }

    const resultado = { [CODIGO_SUBCATEGORIA_IRPJ]: [], [CODIGO_SUBCATEGORIA_CSLL]: [] };
    for (const [empresa, linhas] of Object.entries(porEmpresa)) {
      if (linhas.length < 12) continue;
      resultado[CODIGO_SUBCATEGORIA_IRPJ].push(
        itemCalculado(CODIGO_SUBCATEGORIA_IRPJ, empresa, linhas, (l) => l.irpj + l.irpj_adicional)
      );
      resultado[CODIGO_SUBCATEGORIA_CSLL].push(
        itemCalculado(CODIGO_SUBCATEGORIA_CSLL, empresa, linhas, (l) => l.csll)
      );
    }
    return resultado;
  }, [impostosCalculados]);

  const impostosCalculadosPorEmpresaResumo = useMemo(() => {
    const porEmpresa = {};
    for (const linha of impostosCalculados) {
      if (!porEmpresa[linha.empresa]) porEmpresa[linha.empresa] = [];
      porEmpresa[linha.empresa].push(linha);
    }
    return Object.entries(porEmpresa).map(([empresa, linhas]) => {
      const ordenadas = [...linhas].sort((a, b) => a.mes - b.mes);
      return {
        empresa,
        totalCompensacao: ordenadas.reduce((acc, l) => acc + l.compensacao_utilizada, 0),
        saldoFinal: ordenadas[ordenadas.length - 1]?.saldo_prejuizo_fiscal_final ?? 0,
      };
    });
  }, [impostosCalculados]);

  // Hierarquia de 2 níveis: Categoria Pai (categoria_pai_id nulo) > Subcategoria (aponta pra pai).
  // Itens só existem dentro de subcategorias — a categoria pai nunca tem itens diretos.
  const categoriasOrdenadas = useMemo(
    () => [...categorias].sort((a, b) => (a.ordem - b.ordem) || a.codigo.localeCompare(b.codigo)),
    [categorias]
  );

  const categoriasTopoOrdenadas = useMemo(
    () => categoriasOrdenadas.filter((c) => !c.categoria_pai_id),
    [categoriasOrdenadas]
  );

  const subcategoriasOrdenadas = useMemo(
    () => categoriasOrdenadas.filter((c) => c.categoria_pai_id),
    [categoriasOrdenadas]
  );

  const arvoreCategorias = useMemo(() => categoriasTopoOrdenadas.map((categoriaPai) => {
    const subcategorias = subcategoriasOrdenadas
      .filter((s) => s.categoria_pai_id === categoriaPai.id)
      .map((sub) => {
        const itensManuais = itensComCalculo
          .filter((i) => i.categoria_id === sub.id)
          .sort((a, b) => a.nome.localeCompare(b.nome));
        const itensSub = [...itensManuais, ...(itensCalculadosPorSubcategoria[sub.codigo] || [])];
        const projecao = somarProjecoes(itensSub.map((i) => i.projecao));
        const valorAtual = itensManuais.reduce((acc, i) => acc + Number(i.valorBase || 0), 0);
        return { categoria: sub, itens: itensSub, projecao, total: calcTotalAnual(projecao), valorAtual };
      });
    const projecao = somarProjecoes(subcategorias.map((s) => s.projecao));
    const valorAtual = subcategorias.reduce((acc, s) => acc + s.valorAtual, 0);
    return { categoria: categoriaPai, subcategorias, projecao, total: calcTotalAnual(projecao), valorAtual };
  }), [categoriasTopoOrdenadas, subcategoriasOrdenadas, itensComCalculo, itensCalculadosPorSubcategoria]);

  const totalGeralProjecao = useMemo(() => somarProjecoes(arvoreCategorias.map((l) => l.projecao)), [arvoreCategorias]);
  const totalOrcadoBase = itensComCalculo.reduce((acc, i) => acc + Number(i.valorBase || 0), 0);
  const totalProjetadoAno = calcTotalAnual(totalGeralProjecao);

  const kpiItems = [
    { label: "Total Orçado (Base)", value: loading ? "—" : formatCurrency(totalOrcadoBase), color: C.brand },
    { label: "Total Projetado no Ano", value: loading ? "—" : formatCurrency(totalProjetadoAno), color: C.success },
    { label: "Itens Cadastrados", value: loading ? "—" : String(itens.length), color: C.text },
    { label: "Categorias", value: loading ? "—" : String(categorias.length), color: C.text },
  ];

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    const linhas = [];
    const linhaBase = (codigo, item, valorAtual, projecao, total, extra = {}) => ({
      "Código": codigo,
      "Item": item,
      "Valor Atual": valorAtual,
      "Vig. Valor Início": "",
      "Vig. Valor Fim": "",
      "Qtde Atual": "",
      "Valor Unit.": "",
      "Expansões": "",
      "Reajustes": "",
      ...extra,
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, projecao[i]])),
      "Total": total,
    });

    arvoreCategorias.forEach((paiLinha) => {
      linhas.push(linhaBase(paiLinha.categoria.codigo, paiLinha.categoria.nome, paiLinha.valorAtual, paiLinha.projecao, paiLinha.total));
      paiLinha.subcategorias.forEach((subLinha) => {
        linhas.push(linhaBase(subLinha.categoria.codigo, `  ${subLinha.categoria.nome}`, subLinha.valorAtual, subLinha.projecao, subLinha.total));
        subLinha.itens.forEach((item) => {
          linhas.push(linhaBase("", `    ${item.nome}`, item.valorBase, item.projecao, item.total, {
            "Vig. Valor Início": item.mes_inicio_valor ? MESES_ABREV[item.mes_inicio_valor - 1] : "",
            "Vig. Valor Fim": item.mes_fim_valor ? MESES_ABREV[item.mes_fim_valor - 1] : "",
            "Qtde Atual": item.qtde_usuario_atual,
            "Valor Unit.": item.valorUnitario,
            "Expansões": resumoExpansoes(item.expansoes),
            "Reajustes": resumoReajustes(item.reajustes),
          }));
        });
      });
    });
    linhas.push(linhaBase("", "Total Geral", totalOrcadoBase, totalGeralProjecao, totalProjetadoAno));

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Impostos".slice(0, 31));
    const nomeArquivo = `Impostos_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  // ── Modal: Categoria ──────────────────────────────────────────────────────
  function abrirModalCategoria(categoria = null, categoriaPaiIdPadrao = null) {
    setErroFormCategoria("");
    if (categoria) {
      setEditandoCategoria(categoria);
      setFormCategoria({
        codigo: categoria.codigo, nome: categoria.nome,
        categoria_pai_id: categoria.categoria_pai_id ? String(categoria.categoria_pai_id) : "",
        ordem: String(categoria.ordem ?? 0),
      });
    } else {
      setEditandoCategoria(null);
      setFormCategoria({ ...FORM_CATEGORIA_VAZIO, categoria_pai_id: categoriaPaiIdPadrao ? String(categoriaPaiIdPadrao) : "" });
    }
    setModalCategoriaAberto(true);
  }

  async function salvarCategoria() {
    const codigo = formCategoria.codigo.trim();
    const nome = formCategoria.nome.trim();
    const ordem = parseInt(formCategoria.ordem, 10);
    const categoriaPaiId = formCategoria.categoria_pai_id ? parseInt(formCategoria.categoria_pai_id, 10) : null;
    if (!codigo) return setErroFormCategoria("Informe o código.");
    if (!nome) return setErroFormCategoria("Informe o nome.");

    setSalvandoCategoria(true);
    setErroFormCategoria("");
    try {
      const payload = { codigo, nome, categoria_pai_id: categoriaPaiId, ordem: Number.isNaN(ordem) ? 0 : ordem };
      if (editandoCategoria) await putCategoriaImposto(editandoCategoria.id, payload);
      else await postCategoriaImposto(payload);
      setModalCategoriaAberto(false);
      await carregarTudo(ano);
    } catch (e) {
      setErroFormCategoria(e.message);
    } finally {
      setSalvandoCategoria(false);
    }
  }

  async function excluirCategoria(categoria) {
    if (!categoria) return;
    if (!window.confirm(`Excluir "${categoria.codigo} ${categoria.nome}"? Só é possível se não houver itens/subcategorias vinculados.`)) return;
    try {
      await deleteCategoriaImposto(categoria.id);
      await carregarTudo(ano);
    } catch (e) {
      setErro(e.message);
    }
  }

  // ── Modal: Item (Imposto) ─────────────────────────────────────────────────
  function abrirModalItem(item = null, categoriaIdPadrao = null) {
    setErroFormItem("");
    if (item) {
      setEditandoItem(item);
      setFormItem({
        categoria_id: String(item.categoria_id),
        nome: item.nome,
        valor_atual: String(item.valor_atual),
        mes_inicio_valor: item.mes_inicio_valor ? String(item.mes_inicio_valor) : "",
        mes_fim_valor: item.mes_fim_valor ? String(item.mes_fim_valor) : "",
        qtde_usuario_atual: String(item.qtde_usuario_atual),
        reajustes: (item.reajustes || []).map((r) => ({
          tipo_indice: r.tipo_indice, mes: String(r.mes), percentual: String(r.percentual),
        })),
        expansoes: (item.expansoes || []).map((e) => ({
          qtde_usuario_novo: String(e.qtde_usuario_novo), mes_inicio: String(e.mes_inicio), mes_fim: String(e.mes_fim),
        })),
      });
    } else {
      setEditandoItem(null);
      setFormItem({ ...FORM_ITEM_VAZIO, categoria_id: categoriaIdPadrao ? String(categoriaIdPadrao) : "" });
    }
    setModalItemAberto(true);
  }

  function adicionarReajuste() {
    setFormItem((f) => ({ ...f, reajustes: [...(f.reajustes || []), { tipo_indice: "", mes: "", percentual: "" }] }));
  }

  function atualizarReajuste(indice, campo, valor) {
    setFormItem((f) => {
      const reajustes = [...(f.reajustes || [])];
      reajustes[indice] = { ...reajustes[indice], [campo]: valor };
      return { ...f, reajustes };
    });
  }

  function removerReajuste(indice) {
    setFormItem((f) => ({ ...f, reajustes: (f.reajustes || []).filter((_, i) => i !== indice) }));
  }

  function adicionarExpansao() {
    setFormItem((f) => ({ ...f, expansoes: [...(f.expansoes || []), { qtde_usuario_novo: "", mes_inicio: "", mes_fim: "" }] }));
  }

  function atualizarExpansao(indice, campo, valor) {
    setFormItem((f) => {
      const expansoes = [...(f.expansoes || [])];
      expansoes[indice] = { ...expansoes[indice], [campo]: valor };
      return { ...f, expansoes };
    });
  }

  function removerExpansao(indice) {
    setFormItem((f) => ({ ...f, expansoes: (f.expansoes || []).filter((_, i) => i !== indice) }));
  }

  async function salvarItem() {
    const categoriaId = parseInt(formItem.categoria_id, 10);
    const nome = formItem.nome.trim();
    const valorAtual = parseFloat(formItem.valor_atual);
    const mesInicioValor = formItem.mes_inicio_valor ? parseInt(formItem.mes_inicio_valor, 10) : null;
    const mesFimValor = formItem.mes_fim_valor ? parseInt(formItem.mes_fim_valor, 10) : null;
    const qtdeAtual = parseInt(formItem.qtde_usuario_atual, 10);

    if (!categoriaId) return setErroFormItem("Selecione a categoria.");
    if (!nome) return setErroFormItem("Informe o nome do item.");
    if (Number.isNaN(valorAtual) || valorAtual < 0) return setErroFormItem("Informe um Valor Atual válido.");
    if (Boolean(mesInicioValor) !== Boolean(mesFimValor)) return setErroFormItem("Informe Mês Início e Mês Fim do Valor Atual juntos.");
    if (mesInicioValor && mesFimValor && mesFimValor < mesInicioValor) return setErroFormItem("Mês Fim do Valor Atual não pode ser anterior ao Mês Início.");
    if (Number.isNaN(qtdeAtual) || qtdeAtual < 0) return setErroFormItem("Informe uma Qtde Usuário Atual válida.");
    const reajusteIncompleto = (formItem.reajustes || []).some(
      (r) => !r.tipo_indice || !r.mes || r.percentual === "" || Number.isNaN(parseFloat(r.percentual))
    );
    if (reajusteIncompleto) {
      return setErroFormItem("Preencha Tipo, Mês e Percentual em todos os reajustes (ou remova a linha incompleta).");
    }
    const expansaoIncompleta = (formItem.expansoes || []).some(
      (e) => e.qtde_usuario_novo === "" || Number.isNaN(parseInt(e.qtde_usuario_novo, 10)) || !e.mes_inicio || !e.mes_fim
    );
    if (expansaoIncompleta) {
      return setErroFormItem("Preencha Nova Qtde, Mês Início e Mês Fim em todas as expansões (ou remova a linha incompleta).");
    }
    const expansaoMesInvalido = (formItem.expansoes || []).some(
      (e) => e.mes_inicio && e.mes_fim && parseInt(e.mes_fim, 10) < parseInt(e.mes_inicio, 10)
    );
    if (expansaoMesInvalido) {
      return setErroFormItem("Em uma expansão, o Mês Fim não pode ser anterior ao Mês Início.");
    }

    setSalvandoItem(true);
    setErroFormItem("");
    try {
      const payload = {
        categoria_id: categoriaId, nome, ano,
        valor_atual: valorAtual, mes_inicio_valor: mesInicioValor, mes_fim_valor: mesFimValor,
        qtde_usuario_atual: qtdeAtual,
        reajustes: reajustesDoForm(formItem),
        expansoes: expansoesDoForm(formItem),
      };
      if (editandoItem) await putImposto(editandoItem.id, payload);
      else await postImposto(payload);
      setModalItemAberto(false);
      await carregarTudo(ano);
    } catch (e) {
      setErroFormItem(e.message);
    } finally {
      setSalvandoItem(false);
    }
  }

  async function excluirItem(item) {
    if (!item) return;
    if (!window.confirm(`Excluir o item "${item.nome}"?`)) return;
    try {
      await deleteImposto(item.id);
      if (selectedItemId === item.id) setSelectedItemId(null);
      await carregarTudo(ano);
    } catch (e) {
      setErro(e.message);
    }
  }

  const itemPreview = construirItemPreview(formItem);
  const previewBase = calcValorMensalBase(itemPreview);
  const previewProjecao = calcProjecaoMensal(itemPreview);
  const previewTotal = calcTotalAnual(previewProjecao);

  const COLSPAN_TOTAL = 23;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Impostos</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* KPIs */}
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRow items={kpiItems} C={C} />
          </section>

          {/* Tabela principal */}
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
                Impostos · {ano}
              </h2>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={exportarParaExcel}
                  disabled={loading || categoriasTopoOrdenadas.length === 0}
                  title="Exportar dados para Excel"
                  style={{ ...btnStyle(C.success, true), opacity: loading || categoriasTopoOrdenadas.length === 0 ? 0.45 : 1, cursor: loading || categoriasTopoOrdenadas.length === 0 ? "not-allowed" : "pointer" }}
                >
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                <button onClick={() => abrirModalCategoria(null)} style={btnStyle(C.brand, true)}>
                  <FolderPlus size={15} /> Nova Categoria
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : categoriasTopoOrdenadas.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1800 }}>
                    <thead>
                      <tr>
                        <th style={thStyle(C)}>Código</th>
                        <th style={{ ...thStyle(C), textAlign: "left" }}>Item</th>
                        <th style={thStyle(C)}>Valor Atual</th>
                        <th style={thStyle(C)} title="Vigência do Valor Atual — fora desse período o valor conta como R$ 0,00">Vig. Início</th>
                        <th style={thStyle(C)} title="Vigência do Valor Atual — fora desse período o valor conta como R$ 0,00">Vig. Fim</th>
                        <th style={thStyle(C)}>Qtde Atual</th>
                        <th style={thStyle(C)}>Valor Unit.</th>
                        <th style={{ ...thStyle(C), textAlign: "left" }} title="Janelas de nova quantidade total de usuários, não o incremento">Expansões</th>
                        <th style={{ ...thStyle(C), textAlign: "left" }}>Reajustes</th>
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                        <th style={thStyle(C)}>Total</th>
                        <th style={thStyle(C)}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arvoreCategorias.map((paiLinha) => {
                        const paiColapsada = categoriasColapsadas.has(paiLinha.categoria.id);
                        return (
                        <Fragment key={paiLinha.categoria.id}>
                          <tr
                            onClick={() => toggleCategoria(paiLinha.categoria.id)}
                            style={{ background: C.categoriaBg, fontWeight: 700, cursor: "pointer" }}
                          >
                            <td style={tdStyle(C, true)}>
                              <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start" }}>
                                {paiColapsada ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                {paiLinha.categoria.codigo}
                              </div>
                            </td>
                            <td style={{ ...tdStyle(C, true), textAlign: "left" }}>
                              {paiLinha.categoria.nome}
                              {paiColapsada && (
                                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                  ({paiLinha.subcategorias.length} {paiLinha.subcategorias.length === 1 ? "subcategoria" : "subcategorias"})
                                </span>
                              )}
                            </td>
                            <td style={tdStyle(C)}>{formatCurrency(paiLinha.valorAtual)}</td>
                            <td style={tdStyle(C)} colSpan={6} />
                            {paiLinha.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                            <td style={tdStyle(C)}>{formatCurrency(paiLinha.total)}</td>
                            <td style={tdStyle(C)} onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                <IconBtn title="Adicionar subcategoria" onClick={() => abrirModalCategoria(null, paiLinha.categoria.id)} C={C}><Plus size={13} /></IconBtn>
                                <IconBtn title="Editar categoria" onClick={() => abrirModalCategoria(paiLinha.categoria)} C={C}><Pencil size={13} /></IconBtn>
                                <IconBtn title="Excluir categoria" onClick={() => excluirCategoria(paiLinha.categoria)} C={C} danger><Trash2 size={13} /></IconBtn>
                              </div>
                            </td>
                          </tr>

                          {!paiColapsada && paiLinha.subcategorias.length === 0 && (
                            <tr>
                              <td colSpan={COLSPAN_TOTAL} style={{ padding: "8px 12px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                                Nenhuma subcategoria cadastrada nesta categoria.
                              </td>
                            </tr>
                          )}

                          {!paiColapsada && paiLinha.subcategorias.map((subLinha) => {
                            const subColapsada = categoriasColapsadas.has(subLinha.categoria.id);
                            return (
                            <Fragment key={subLinha.categoria.id}>
                              <tr
                                onClick={() => toggleCategoria(subLinha.categoria.id)}
                                style={{ background: C.subcategoriaBg, fontWeight: 600, cursor: "pointer" }}
                              >
                                <td style={tdStyle(C, true)}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-start", paddingLeft: 16 }}>
                                    {subColapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                                    {subLinha.categoria.codigo}
                                  </div>
                                </td>
                                <td style={{ ...tdStyle(C, true), textAlign: "left" }}>
                                  {subLinha.categoria.nome}
                                  {subColapsada && (
                                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: C.muted }}>
                                      ({subLinha.itens.length} {subLinha.itens.length === 1 ? "item" : "itens"})
                                    </span>
                                  )}
                                </td>
                                <td style={tdStyle(C)}>{formatCurrency(subLinha.valorAtual)}</td>
                                <td style={tdStyle(C)} colSpan={6} />
                                {subLinha.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                                <td style={tdStyle(C)}>{formatCurrency(subLinha.total)}</td>
                                <td style={tdStyle(C)} onClick={(e) => e.stopPropagation()}>
                                  <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                    <IconBtn title="Editar subcategoria" onClick={() => abrirModalCategoria(subLinha.categoria)} C={C}><Pencil size={13} /></IconBtn>
                                    <IconBtn title="Excluir subcategoria" onClick={() => excluirCategoria(subLinha.categoria)} C={C} danger><Trash2 size={13} /></IconBtn>
                                  </div>
                                </td>
                              </tr>

                              {!subColapsada && subLinha.itens.length === 0 && (
                                <tr>
                                  <td colSpan={COLSPAN_TOTAL} style={{ padding: "8px 12px 8px 40px", fontSize: 11, fontStyle: "italic", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                                    Nenhum item cadastrado nesta subcategoria.
                                  </td>
                                </tr>
                              )}

                              {!subColapsada && subLinha.itens.map((item) => (
                                <tr
                                  key={item.id}
                                  onClick={() => setSelectedItemId(item.id === selectedItemId ? null : item.id)}
                                  style={{ background: item.id === selectedItemId ? C.bgActive : "transparent", cursor: "pointer" }}
                                >
                                  <td style={tdStyle(C)}>—</td>
                                  <td style={{ ...tdStyle(C), textAlign: "left" }}>
                                    {item.nome}
                                    {item.calculado && (
                                      <span style={{
                                        display: "inline-block", fontSize: 9.5, fontWeight: 700, borderRadius: 999,
                                        padding: "1px 7px", marginLeft: 8, background: `${C.brand}18`, color: C.brand,
                                        border: `1px solid ${C.brand}`,
                                      }}>
                                        calculado · DRE por Empresa
                                      </span>
                                    )}
                                  </td>
                                  <td style={tdStyle(C)}>{item.calculado ? "—" : formatCurrency(item.valorBase)}</td>
                                  <td style={tdStyle(C)}>{item.mes_inicio_valor ? MESES_ABREV[item.mes_inicio_valor - 1] : "—"}</td>
                                  <td style={tdStyle(C)}>{item.mes_fim_valor ? MESES_ABREV[item.mes_fim_valor - 1] : "—"}</td>
                                  <td style={tdStyle(C)}>{item.calculado ? "—" : item.qtde_usuario_atual}</td>
                                  <td style={tdStyle(C)}>{item.calculado ? "—" : formatCurrency(item.valorUnitario)}</td>
                                  <td style={{ ...tdStyle(C), textAlign: "left" }} title={resumoExpansoes(item.expansoes)}>
                                    {item.expansoes && item.expansoes.length > 0 ? resumoExpansoes(item.expansoes) : "—"}
                                  </td>
                                  <td style={{ ...tdStyle(C), textAlign: "left" }} title={resumoReajustes(item.reajustes)}>
                                    {item.reajustes && item.reajustes.length > 0 ? resumoReajustes(item.reajustes) : "—"}
                                  </td>
                                  {item.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                                  <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(item.total)}</td>
                                  <td style={tdStyle(C)} onClick={(e) => e.stopPropagation()}>
                                    {!item.calculado && (
                                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                        <IconBtn title="Editar item" onClick={() => abrirModalItem(item)} C={C}><Pencil size={13} /></IconBtn>
                                        <IconBtn title="Excluir item" onClick={() => excluirItem(item)} C={C} danger><Trash2 size={13} /></IconBtn>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                            );
                          })}
                        </Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ ...tdStyle(C, true), textAlign: "left" }} colSpan={2}>Total Geral</td>
                        <td style={tdStyle(C)}>{formatCurrency(totalOrcadoBase)}</td>
                        <td style={tdStyle(C)} colSpan={6} />
                        {totalGeralProjecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyle(C)}>{formatCurrency(totalProjetadoAno)}</td>
                        <td style={tdStyle(C)} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Prejuízo fiscal compensado/remanescente por empresa — não é uma linha do DRE (só
              afeta a base tributável de IRPJ/CSLL acima), então fica como nota informativa em
              vez de linha na árvore (ver services/dre_empresa_svc.py). */}
          {impostosCalculadosPorEmpresaResumo.length > 0 && (
            <section style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {impostosCalculadosPorEmpresaResumo.map(({ empresa, totalCompensacao, saldoFinal }) => (
                <div key={empresa} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 14px", fontSize: 11.5, color: C.muted }}>
                  <strong style={{ color: C.text }}>{empresa}</strong> · Prejuízo fiscal compensado no ano: {formatCurrency(totalCompensacao)} · Saldo remanescente: {formatCurrency(saldoFinal)}
                </div>
              ))}
            </section>
          )}
        </main>
      </div>

      {/* Modal: Categoria */}
      {modalCategoriaAberto && (
        <ModalShell C={C} title={editandoCategoria ? "Editar Categoria" : "Nova Categoria"} onClose={() => setModalCategoriaAberto(false)} maxWidth={420}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Campo label="Categoria Pai" C={C}>
              <select style={inputStyle(C)} value={formCategoria.categoria_pai_id}
                onChange={(e) => setFormCategoria({ ...formCategoria, categoria_pai_id: e.target.value })}>
                <option value="">— Nenhuma (categoria de nível 1) —</option>
                {categoriasTopoOrdenadas
                  .filter((c) => !editandoCategoria || c.id !== editandoCategoria.id)
                  .map((c) => <option key={c.id} value={c.id}>{c.codigo} {c.nome}</option>)}
              </select>
            </Campo>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Deixe em branco para uma categoria pai (ex: 5.1). Selecione uma categoria pai para criar uma subcategoria dela (ex: 5.1.03) — só as subcategorias recebem itens.
            </p>
            <Campo label="Código" C={C}>
              <input style={inputStyle(C)} type="text" placeholder="Ex: 5.1.03" value={formCategoria.codigo}
                onChange={(e) => setFormCategoria({ ...formCategoria, codigo: e.target.value })} />
            </Campo>
            <Campo label="Nome" C={C}>
              <input style={inputStyle(C)} type="text" placeholder="Ex: PIS/COFINS" value={formCategoria.nome}
                onChange={(e) => setFormCategoria({ ...formCategoria, nome: e.target.value })} />
            </Campo>
            <Campo label="Ordem de Exibição" C={C}>
              <input style={inputStyle(C)} type="number" step="1" value={formCategoria.ordem}
                onChange={(e) => setFormCategoria({ ...formCategoria, ordem: e.target.value })} />
            </Campo>
            {erroFormCategoria && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroFormCategoria}</p>}
          </div>
          <ModalFooter
            C={C} salvando={salvandoCategoria}
            onCancel={() => setModalCategoriaAberto(false)}
            onSave={salvarCategoria}
            labelSalvar={editandoCategoria ? "Atualizar" : "Cadastrar"}
          />
        </ModalShell>
      )}

      {/* Modal: Item (Imposto) */}
      {modalItemAberto && (
        <ModalShell C={C} title={editandoItem ? "Editar Item" : "Novo Item"} onClose={() => setModalItemAberto(false)} maxWidth={620}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Campo label="Subcategoria" C={C}>
              <ComboBoxFiltravel
                C={C}
                value={formItem.categoria_id}
                onChange={(v) => setFormItem({ ...formItem, categoria_id: v })}
                options={subcategoriasOrdenadas.map((c) => {
                  const pai = categorias.find((p) => p.id === c.categoria_pai_id);
                  return { value: c.id, label: `${c.codigo} ${c.nome}${pai ? ` — sob ${pai.codigo} ${pai.nome}` : ""}` };
                })}
              />
            </Campo>

            <Campo label="Nome do Item" C={C}>
              <input style={inputStyle(C)} type="text" placeholder="Ex: IRPJ do mês" value={formItem.nome}
                onChange={(e) => setFormItem({ ...formItem, nome: e.target.value })} />
            </Campo>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Valor Atual (R$)" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" placeholder="0.00" value={formItem.valor_atual}
                  onChange={(e) => setFormItem({ ...formItem, valor_atual: e.target.value })} />
              </Campo>
              <Campo label="Qtde Atual" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="1" value={formItem.qtde_usuario_atual}
                  onChange={(e) => setFormItem({ ...formItem, qtde_usuario_atual: e.target.value })} />
              </Campo>
            </div>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Valor Mensal Base (calculado, Valor Atual × Qtde Atual): {formatCurrency(previewBase)}
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Mês Início (Valor Atual)" C={C}>
                <select style={inputStyle(C)} value={formItem.mes_inicio_valor}
                  onChange={(e) => setFormItem({ ...formItem, mes_inicio_valor: e.target.value })}>
                  <option value="">—</option>
                  {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Campo>
              <Campo label="Mês Fim (Valor Atual)" C={C}>
                <select style={inputStyle(C)} value={formItem.mes_fim_valor}
                  onChange={(e) => setFormItem({ ...formItem, mes_fim_valor: e.target.value })}>
                  <option value="">—</option>
                  {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Campo>
            </div>
            <p style={{ margin: "-6px 0 0", fontSize: 11, color: C.muted }}>
              Deixe em branco para o Valor Atual valer o ano inteiro. Preenchidos, o valor só entra na projeção
              dentro desse período (fora dele conta como R$ 0,00) — mesma lógica das janelas abaixo, para a Nova Qtde.
            </p>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
                  Nova Qtde de Usuários
                </label>
                <button type="button" onClick={adicionarExpansao} style={{
                  display: "flex", alignItems: "center", gap: 4, background: "transparent",
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px",
                  color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <Plus size={12} /> Adicionar Expansão
                </button>
              </div>
              {(formItem.expansoes || []).length === 0 && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                  Nenhuma expansão cadastrada — o item usa só a Qtde Atual o ano todo.
                </p>
              )}
              {(formItem.expansoes || []).map((e, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <input style={inputStyle(C)} type="number" min="0" step="1" placeholder="Nova Qtde (Total)" value={e.qtde_usuario_novo}
                    onChange={(ev) => atualizarExpansao(i, "qtde_usuario_novo", ev.target.value)} />
                  <select style={inputStyle(C)} value={e.mes_inicio} onChange={(ev) => atualizarExpansao(i, "mes_inicio", ev.target.value)}>
                    <option value="">Mês Início…</option>
                    {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <select style={inputStyle(C)} value={e.mes_fim} onChange={(ev) => atualizarExpansao(i, "mes_fim", ev.target.value)}>
                    <option value="">Mês Fim…</option>
                    {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <IconBtn title="Remover expansão" onClick={() => removerExpansao(i)} C={C} danger><Trash2 size={13} /></IconBtn>
                </div>
              ))}
              <p style={{ margin: "4px 0 0", fontSize: 11, color: C.muted }}>
                Nova Qtde é o total após a mudança (não o incremento). Fora das janelas cadastradas, a quantidade volta a ser a Qtde Atual.
              </p>
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
                  Reajustes de Valor
                </label>
                <button type="button" onClick={adicionarReajuste} style={{
                  display: "flex", alignItems: "center", gap: 4, background: "transparent",
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px",
                  color: C.brand, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>
                  <Plus size={12} /> Adicionar Reajuste
                </button>
              </div>
              {(formItem.reajustes || []).length === 0 && (
                <p style={{ margin: 0, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                  Nenhum reajuste cadastrado — o item usa só o Valor Atual o ano todo.
                </p>
              )}
              {(formItem.reajustes || []).map((r, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                  <select style={inputStyle(C)} value={r.tipo_indice} onChange={(e) => atualizarReajuste(i, "tipo_indice", e.target.value)}>
                    <option value="">Tipo…</option>
                    <option value="inflacao">Inflação</option>
                    <option value="reajuste">Reajuste</option>
                  </select>
                  <select style={inputStyle(C)} value={r.mes} onChange={(e) => atualizarReajuste(i, "mes", e.target.value)}>
                    <option value="">Mês…</option>
                    {OPCOES_MES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input style={inputStyle(C)} type="number" step="0.01" placeholder="% Ex: 4,00" value={r.percentual}
                    onChange={(e) => atualizarReajuste(i, "percentual", e.target.value)} />
                  <IconBtn title="Remover reajuste" onClick={() => removerReajuste(i)} C={C} danger><Trash2 size={13} /></IconBtn>
                </div>
              ))}
            </div>

            {erroFormItem && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroFormItem}</p>}

            {/* Pré-visualização da projeção mensal */}
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Pré-visualização · Projeção Mensal
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 560 }}>
                  <thead>
                    <tr>
                      {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                      <th style={thStyle(C)}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {previewProjecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                      <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(previewTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <ModalFooter
            C={C} salvando={salvandoItem}
            onCancel={() => setModalItemAberto(false)}
            onSave={salvarItem}
            labelSalvar={editandoItem ? "Atualizar" : "Cadastrar"}
          />
        </ModalShell>
      )}
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function thStyle(C) {
  return {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}

function IconBtn({ children, onClick, title, C, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`,
        background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ModalShell({ title, children, onClose, C, maxWidth = 480 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ C, salvando, onCancel, onSave, labelSalvar }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
      <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
        onClick={onCancel}>
        Cancelar
      </button>
      <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }}
        onClick={onSave} disabled={salvando}>
        {salvando ? "Salvando..." : labelSalvar}
      </button>
    </div>
  );
}
